import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  Flex,
  Text,
  TextField,
  TagBadge,
  useToast,
} from '@chatsdet/ui';
import { WarningCircle } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { toastErrorOptionsFor } from '../../../lib/toast-error-options';
import type { Runbook, RunbookRun } from '../../../features/runbooks/types';
import {
  ActionParamsEditor,
  type ActionParamsValues,
} from '../../pages/suites/external/ActionParamsEditor';

interface RunbookTriggerDialogProps {
  runbook: Runbook | null;
  propertyId: string;
  onOpenChange: (open: boolean) => void;
  /** Called with the new run so the caller can open its detail immediately. */
  onRunStarted: (run: RunbookRun) => void;
}

/**
 * Confirm + trigger dialog for one runbook.
 *
 * When `runbook.destructive` is set the submit button stays disabled until
 * the operator types the runbook's own slug — the whole reason the flag
 * exists in the file format is to force a deliberate, typo-proof
 * confirmation before a script that (by the author's own declaration) does
 * something hard to undo runs against real infrastructure.
 */
export function RunbookTriggerDialog({
  runbook,
  propertyId,
  onOpenChange,
  onRunStarted,
}: RunbookTriggerDialogProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [values, setValues] = React.useState<ActionParamsValues>({});
  const [confirmText, setConfirmText] = React.useState('');
  const [sha, setSha] = React.useState('');

  React.useEffect(() => {
    if (runbook) {
      setValues({});
      setConfirmText('');
      setSha('');
    }
  }, [runbook?.id]);

  // Lowercased to match the server, which does the same rather than rejecting a
  // pasted uppercase sha over a difference git itself does not care about.
  const trimmedSha = sha.trim().toLowerCase();
  const shaValid = trimmedSha === '' || /^[0-9a-f]{40}$/.test(trimmedSha);

  const runMutation = useMutation({
    mutationFn: async (): Promise<{ ok: boolean; run: RunbookRun }> => {
      if (!runbook) throw new Error('No runbook selected');
      const res = await fetch(`${API_URL}/org/runbooks/${runbook.id}/run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          inputs: values,
          ...(trimmedSha ? { sha: trimmedSha } : {}),
          // The server now enforces the same typed-confirmation gate for
          // destructive runbooks (422 RUNBOOK_CONFIRMATION_REQUIRED) — this
          // isn't vestigial, don't drop it.
          ...(confirmRequired ? { confirm: confirmText } : {}),
        }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to start run');
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['property-runbook-runs', propertyId] });
      toast.success(`${runbook?.name} queued`);
      onOpenChange(false);
      onRunStarted(data.run);
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to start run',
        toastErrorOptionsFor(error)
      );
    },
  });

  const properties = runbook?.inputSchema ? Object.entries(runbook.inputSchema.properties) : [];
  const confirmRequired = runbook?.destructive ?? false;
  const confirmSatisfied = !confirmRequired || confirmText === runbook?.slug;
  const canRun = confirmSatisfied && shaValid;

  return (
    <DialogTrigger
      isOpen={!!runbook}
      onOpenChange={(open) => {
        if (!open) onOpenChange(false);
      }}
      size="lg"
    >
      <span />
      <Dialog
        title={runbook?.name ?? ''}
        description={runbook?.description ?? undefined}
        footer={
          <>
            <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={runMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant={confirmRequired ? 'destructive' : 'primary'}
              onPress={() => runMutation.mutate()}
              isDisabled={!canRun || runMutation.isPending}
            >
              {runMutation.isPending ? 'Starting…' : 'Run'}
            </Button>
          </>
        }
      >
        <DialogContent>
          <Flex direction="column" gap="md">
            <Flex align="center" gap="sm">
              <Text size="xs" className="text-muted-foreground font-mono">
                {runbook?.slug}
              </Text>
              {runbook?.destructive && <TagBadge name="Destructive" color="red" hideDot />}
            </Flex>

            {runbook !== null && runbook.stage !== null && (
              // The commit is platform-owned for promotions (validated server-side as an
              // ancestor of the default branch and cloned at) rather than a declared
              // runbook input, so it gets its own first-class field here.
              <TextField
                label="Commit"
                description="Full 40-character commit SHA. Leave blank to deploy the tip of the default branch."
                value={sha}
                onChange={setSha}
                errorMessage={shaValid ? undefined : 'Must be a full 40-character commit SHA.'}
              />
            )}

            {properties.length > 0 && runbook?.inputSchema ? (
              <ActionParamsEditor
                schema={runbook.inputSchema}
                values={values}
                onValuesChange={setValues}
              />
            ) : (
              <Text size="xs" className="text-muted-foreground">
                This runbook takes no parameters.
              </Text>
            )}

            {confirmRequired && (
              <Flex direction="column" gap="xs" className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                <Flex align="center" gap="xs">
                  <WarningCircle size={16} className="text-destructive shrink-0" />
                  <Text size="sm" weight="medium">
                    This runbook is marked destructive.
                  </Text>
                </Flex>
                <TextField
                  label={`Type "${runbook?.slug}" to confirm`}
                  value={confirmText}
                  onChange={setConfirmText}
                  autoFocus
                />
              </Flex>
            )}
          </Flex>
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}
